import { Box } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { titleCase } from "../../../../utils";
import { VerificationAppDocumentType } from "../../../app/models";
import { TDocumentData } from "../../models";
import { saveDocumentDataForDocument } from "../../network";
import { cleanupConfigFieldValues } from "../../utils/cleanupConfigFieldValues";
import {
  FILES_NAMESPACE,
  TFileIntentMap,
} from "../../utils/fileIntents";
import {
  buildInitialIdentificationFileIntents,
  processIdentificationFileIntents,
} from "../../utils/identificationDocumentFiles";
import { getEditablePrimaryDocumentData } from "../../utils/pendingDocument";
import { FileContent } from "../FileContent";
import { AccordionShell } from "../StepAccordion/AccordionShell";
import { BinaryContent } from "./index";
import {
  DOCUMENT_TYPE_FIELDS,
  IDENTIFICATION_DOCUMENT_FILE_FIELD,
} from "./fieldDefinitions";
import { IdentificationReportContent } from "./IdentificationReportContent";
import { ScreeningReportContent } from "./ScreeningReportContent";
import type { DocumentTypeRendererProps } from "./DocumentTypeRenderer";

function splitData(data: TDocumentData[]) {
  const binary: TDocumentData[] = [];
  const file: TDocumentData[] = [];
  for (const item of data) {
    if (item.type === "binary") binary.push(item);
    else if (item.type === "file") file.push(item);
  }
  return { binary, file };
}

const EDITABLE_IDENTIFICATION_DOCUMENT_TYPES = new Set<string>([
  VerificationAppDocumentType.Passport,
  VerificationAppDocumentType.Id,
]);

export const PassportRenderer = memo((props: DocumentTypeRendererProps) => {
  const {
    document,
    requestId,
    companyRole,
    comments,
    addComment,
    defaultExpanded,
    allowIdentificationEdit,
    onRefreshDocuments,
  } = props;

  const documentType = document.document.documentType;
  const title = titleCase(documentType);
  const { binary, file } = useMemo(() => splitData(document.data), [document.data]);

  const reportEntries = useMemo(
    () =>
      binary.filter(
        (d) =>
          d.dataId === "IdentificationReport" || d.dataId === "ScreeningReport"
      ),
    [binary]
  );
  const canEditDocument =
    allowIdentificationEdit === true &&
    EDITABLE_IDENTIFICATION_DOCUMENT_TYPES.has(documentType);
  const loadedMainEntry = useMemo(
    () => binary.find((d) => d.dataId === documentType),
    [binary, documentType]
  );
  const mainEntry = useMemo(
    () =>
      loadedMainEntry ??
      (canEditDocument
        ? getEditablePrimaryDocumentData(document)
        : undefined),
    [canEditDocument, document, loadedMainEntry],
  );
  const configFields = DOCUMENT_TYPE_FIELDS[documentType];
  const mainDataId = mainEntry?.dataId;
  const canEditPayload =
    canEditDocument &&
    !!mainEntry;
  const renderedConfigFields = useMemo(
    () =>
      canEditPayload
        ? [...(configFields ?? []), IDENTIFICATION_DOCUMENT_FILE_FIELD]
        : configFields,
    [canEditPayload, configFields],
  );
  const initialValues = useMemo(
    () => {
      const main = (mainEntry?.data as Record<string, unknown> | undefined) ?? {};
      if (!canEditPayload) {
        return main;
      }
      return {
        ...main,
        ...buildInitialIdentificationFileIntents(file),
      };
    },
    [mainEntry?.data, canEditPayload, file],
  );

  const onSavePayload = useCallback(
    async (values: Record<string, unknown>) => {
      const { [FILES_NAMESPACE]: files, ...mainValues } = values as Record<
        string,
        unknown
      > & { [FILES_NAMESPACE]?: TFileIntentMap };

      let effectiveDocument = document.document;
      if (mainDataId) {
        effectiveDocument = await saveDocumentDataForDocument(
          document.document,
          requestId,
          mainDataId,
          cleanupConfigFieldValues(mainValues, renderedConfigFields),
        );
      }
      await processIdentificationFileIntents(
        effectiveDocument.documentId,
        files,
      );
      return effectiveDocument.documentId;
    },
    [document.document, mainDataId, renderedConfigFields, requestId],
  );

  return (
    <AccordionShell
      title={title}
      document={document}
      companyRole={companyRole}
      comments={comments}
      addComment={addComment}
      initialValues={initialValues}
      canEditPayload={canEditPayload}
      requiresPayloadSave={mainEntry?.id === ""}
      onSavePayload={canEditPayload ? onSavePayload : undefined}
      onSaveSuccess={canEditPayload ? onRefreshDocuments : undefined}
      defaultExpanded={defaultExpanded}
    >
      {reportEntries.map((dataItem) =>
        dataItem.dataId === "IdentificationReport" ? (
          <IdentificationReportContent
            key={dataItem.id}
            data={dataItem}
            setDataFields={() => undefined}
            dataFields={{}}
            allDisabled
          />
        ) : (
          <ScreeningReportContent
            key={dataItem.id}
            data={dataItem}
            setDataFields={() => undefined}
            dataFields={{}}
            allDisabled
          />
        )
      )}
      {mainEntry && (
        <BinaryContent
          allDisabled={!canEditPayload}
          data={mainEntry}
          configFields={renderedConfigFields}
          fileItems={canEditPayload ? file : undefined}
          requestId={canEditPayload ? requestId : undefined}
          documentInfo={canEditPayload ? document.document : undefined}
        />
      )}
      {!canEditPayload && file.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {file.map((fileItem) => (
            <FileContent
              key={fileItem.id}
              requestId={requestId}
              documentId={document.document.documentId}
              fileId={fileItem.dataId}
              dataLink={fileItem.dataLink}
              fileInfo={fileItem.fileInfo}
            />
          ))}
        </Box>
      )}
    </AccordionShell>
  );
});
